// Phase MHM-2 · Manifest detail.
import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, ArrowLeft, FileText, QrCode, Tags, ShieldCheck, Scale } from "lucide-react";
import { toast } from "sonner";
import api from "../../services/api";
import MHMSubNav from "../../components/hub/MHMSubNav";
import TrackingIdLink from "../../components/hub/TrackingIdLink";
import RoleGate from "../../components/RoleGate";

function Section({ title, children, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid={testid}>
      <h3 className="font-semibold text-slate-800 mb-2 font-['Outfit']">{title}</h3>
      {children}
    </div>
  );
}

export default function ManifestDetail() {
  const { manifestId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanInput, setScanInput] = useState("");
  const [scanResult, setScanResult] = useState(null);
  const [declared, setDeclared] = useState(0);
  const [actual, setActual] = useState(0);
  const [weightResult, setWeightResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/hub/dispatch/manifests/${manifestId}`);
      setData(data);
      setDeclared(data.manifest?.total_weight_kg || 0);
    } catch (e) { toast.error(e.userMessage || "Load failed"); }
    finally { setLoading(false); }
  }, [manifestId]);
  useEffect(() => { load(); }, [load]);

  const downloadFile = async (path, filename) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.userMessage || "Download failed"); }
  };

  const verifyScan = async () => {
    const ids = scanInput.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);
    try {
      const { data } = await api.post(`/hub/dispatch/manifests/${manifestId}/verify`, { scanned_tracking_ids: ids });
      setScanResult(data);
      if (data.all_match) toast.success("All parcels verified!");
      else toast.warning(`Missing: ${data.missing.length} · Extra: ${data.extra.length}`);
      load();
    } catch (e) { toast.error(e.userMessage || "Verify failed"); }
  };

  const weightCheck = async () => {
    try {
      const { data } = await api.post(`/hub/dispatch/manifests/${manifestId}/weight-check`,
                                       { declared_total_kg: Number(declared), weighbridge_total_kg: Number(actual) });
      setWeightResult(data);
      if (data.flagged) toast.warning(`Mismatch ${data.delta_pct}% (> ${data.tolerance_pct}%) — alert created`);
      else toast.success(`Within tolerance · delta ${data.delta_pct}%`);
      load();
    } catch (e) { toast.error(e.userMessage || "Weight check failed"); }
  };

  if (loading) return (
    <RoleGate allow={["main_hub_manager", "admin", "founder", "ceo"]}>
      <MHMSubNav />
      <div className="max-w-[1200px] mx-auto p-8 flex items-center gap-2 text-slate-500">
        <Loader2 className="h-5 w-5 animate-spin" /> Loading manifest…
      </div>
    </RoleGate>
  );

  const m = data.manifest;

  return (
    <RoleGate allow={["main_hub_manager", "admin", "founder", "ceo"]}>
      <MHMSubNav />
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 space-y-4" data-testid="mhm-manifest-detail">
        <Link to="/dashboard/hub/dispatch" className="text-sm text-slate-500 inline-flex items-center gap-1 hover:text-slate-900">
          <ArrowLeft className="h-4 w-4" /> Back to dispatch
        </Link>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Outfit']" data-testid="mhm-manifest-title">
              {m.manifest_id}
            </h1>
            <p className="text-sm text-slate-500">
              <span className="font-mono">{m.vehicle?.plate}</span> · {m.courier_partner} · {m.parcel_count} parcels · {m.total_weight_kg} kg
            </p>
          </div>
          <span className={`text-xs px-2.5 py-1 rounded-full ${
            m.status === "dispatched_verified" ? "bg-green-100 text-green-800" :
            m.status === "dispatched" ? "bg-blue-100 text-blue-800" : "bg-slate-100"
          }`} data-testid="mhm-manifest-status">{m.status}</span>
        </div>

        {/* Download buttons */}
        <div className="flex flex-wrap gap-2" data-testid="mhm-manifest-downloads">
          <button onClick={() => downloadFile(`/hub/dispatch/manifests/${manifestId}/manifest.pdf`, `manifest_${manifestId}.pdf`)}
                  data-testid="mhm-manifest-pdf-btn"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-violet-600 hover:bg-violet-700 text-white rounded-lg">
            <FileText className="h-4 w-4" /> Manifest PDF
          </button>
          <button onClick={() => downloadFile(`/hub/dispatch/manifests/${manifestId}/labels.pdf`, `labels_${manifestId}.pdf`)}
                  data-testid="mhm-manifest-labels-btn"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-200 hover:bg-slate-50 rounded-lg">
            <Tags className="h-4 w-4" /> Labels PDF
          </button>
          <a target="_blank" rel="noreferrer"
             href={`${process.env.REACT_APP_BACKEND_URL}/api/hub/dispatch/manifests/${manifestId}/qr-sack.png`}
             data-testid="mhm-manifest-qr-btn"
             className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-200 hover:bg-slate-50 rounded-lg">
            <QrCode className="h-4 w-4" /> QR Sack PNG
          </a>
        </div>

        {/* Vehicle + courier */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Section title="Vehicle" testid="mhm-manifest-vehicle">
            <div className="text-sm space-y-0.5">
              <div>Plate: <b className="font-mono">{m.vehicle?.plate}</b></div>
              <div>Type: {m.vehicle?.type}</div>
              <div>Driver: {m.vehicle?.driver_name || "—"}</div>
              <div>Phone: {m.vehicle?.driver_phone || "—"}</div>
            </div>
          </Section>
          <Section title="Courier" testid="mhm-manifest-courier-block">
            <div className="text-sm space-y-0.5">
              <div>Partner: <b>{m.courier_partner}</b></div>
              {m.courier_decider_result?.eta_hours != null && (
                <div>ETA: {m.courier_decider_result.eta_hours}h · ₹{m.courier_decider_result.price_estimate}</div>
              )}
              <div className="text-xs text-slate-500">SLA: {m.courier_decider_result?.sla_class || "—"}</div>
            </div>
          </Section>
          <Section title="Created" testid="mhm-manifest-created">
            <div className="text-sm space-y-0.5">
              <div>{(m.created_at || "").slice(0, 19).replace("T", " ")}</div>
              <div>Hub: {m.hub_id}</div>
            </div>
          </Section>
        </div>

        {/* Verify-scan + Weight-check */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Section title="Verify scan" testid="mhm-manifest-verify-section">
            <textarea data-testid="mhm-manifest-scan-input" value={scanInput}
                      onChange={(e) => setScanInput(e.target.value)}
                      rows={4}
                      placeholder="Paste scanned tracking IDs (one per line)…"
                      className="w-full font-mono text-xs border border-slate-200 rounded-lg px-2 py-1.5 mb-2"/>
            <button onClick={verifyScan}
                    data-testid="mhm-manifest-verify-btn"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-violet-600 hover:bg-violet-700 text-white rounded-lg">
              <ShieldCheck className="h-4 w-4" /> Verify
            </button>
            {scanResult && (
              <div className="mt-3 text-xs space-y-1" data-testid="mhm-manifest-scan-result">
                <div>All match: <b>{scanResult.all_match ? "YES" : "NO"}</b></div>
                <div>Missing: {scanResult.missing.length} · {scanResult.missing.slice(0, 3).join(", ")}{scanResult.missing.length > 3 && "…"}</div>
                <div>Extra: {scanResult.extra.length} · {scanResult.extra.slice(0, 3).join(", ")}{scanResult.extra.length > 3 && "…"}</div>
              </div>
            )}
          </Section>
          <Section title="Weight check" testid="mhm-manifest-weight-section">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <label className="block text-xs text-slate-500 mb-1">Declared (kg)</label>
                <input type="number" value={declared} onChange={(e) => setDeclared(e.target.value)}
                       data-testid="mhm-manifest-weight-declared"
                       className="w-full border border-slate-200 rounded-lg px-2 py-1.5"/>
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Weighbridge (kg)</label>
                <input type="number" value={actual} onChange={(e) => setActual(e.target.value)}
                       data-testid="mhm-manifest-weight-actual"
                       className="w-full border border-slate-200 rounded-lg px-2 py-1.5"/>
              </div>
            </div>
            <button onClick={weightCheck}
                    data-testid="mhm-manifest-weight-btn"
                    className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-violet-600 hover:bg-violet-700 text-white rounded-lg">
              <Scale className="h-4 w-4" /> Check weight
            </button>
            {weightResult && (
              <div className="mt-3 text-xs space-y-1" data-testid="mhm-manifest-weight-result">
                <div>Delta: <b>{weightResult.delta_kg} kg ({weightResult.delta_pct}%)</b></div>
                <div className={weightResult.flagged ? "text-rose-600" : "text-green-600"}>
                  {weightResult.flagged
                    ? `⚠ Mismatch > ${weightResult.tolerance_pct}% — yellow alert created`
                    : `✓ Within ${weightResult.tolerance_pct}% tolerance`}
                </div>
              </div>
            )}
          </Section>
        </div>

        {/* Parcel table */}
        <Section title={`Parcels · ${data.parcels.length}`} testid="mhm-manifest-parcels">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500">
                <tr><th className="text-left py-1">Tracking ID</th><th className="text-left">Drop city</th><th className="text-left">Pincode</th><th className="text-right">Weight</th><th className="text-right">Amount</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.parcels.map((p) => (
                  <tr key={p.tracking_id} data-testid={`mhm-manifest-parcel-${p.tracking_id}`}>
                    <td className="py-1 font-mono text-xs">{p.tracking_id}</td>
                    <td>{p.drop_city || "—"}</td>
                    <td className="text-xs">{p.drop_pincode || "—"}</td>
                    <td className="text-right text-xs">
                      {(p.items || [{ weight_kg: p.weight_kg || 0.5 }])
                        .reduce((s, i) => s + (i.weight_kg || 0), 0).toFixed(2)} kg
                    </td>
                    <td className="text-right text-xs">₹{p.final_price || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      </div>
    </RoleGate>
  );
}
